// Presentation only. Policy eligibility, route selection and amounts come from the server.
const records = value => Array.isArray(value) ? value : [];
export const dclText = value => typeof value === 'string' || typeof value === 'number' ? String(value) : '';
const hasPolicy = assessment => Boolean(dclText(assessment?.policyId).trim() && assessment?.policyVersion !== null && assessment?.policyVersion !== undefined);
const statusStyles = {
  eligible:{label:'DCL eligible',color:'#386452',background:'#EDF7F1',border:'#CDE2D7'},
  failed:{label:'Not DCL eligible',color:'#964C43',background:'#FFF1EF',border:'#E9D0CD'},
  data:{label:'Needs data',color:'#536C8E',background:'#F1F5FB',border:'#D9E2EF'},
  unknown:{label:'Needs review',color:'#645E83',background:'#F3F3FC',border:'#DBDDF0'},
  stale:{label:'Needs review',color:'#876B2D',background:'#FFF8E9',border:'#E8DDBE'},
  unassessed:{label:'Not assessed',color:'#67717E',background:'#F5F7F9',border:'#DDE2E8'},
  loading:{label:'Loading assessment',color:'#536C8E',background:'#F1F5FB',border:'#D9E2EF'},
  error:{label:'Assessment unavailable',color:'#876B2D',background:'#FFF8E9',border:'#E8DDBE'},
};
export function formatDclMoney(value, fallback = '—') {
  if (!value || typeof value !== 'object') return fallback;
  const amount=dclText(value.amount), currency=dclText(value.currency);
  if (!/^-?\d+(?:\.\d+)?$/.test(amount) || !/^[A-Z]{3}$/.test(currency)) return fallback;
  const sign=amount.startsWith('-')?'-':'', [whole,fraction]=amount.replace(/^-/, '').split('.');
  return `${currency} ${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g,',')}${fraction!==undefined?`.${fraction}`:''}`;
}
export function formatDclTimestamp(value, fallback = 'Not supplied') {
  const input=dclText(value);
  if(!input)return fallback;
  const parsed=new Date(input);
  if(Number.isNaN(parsed.getTime()))return 'Date unavailable';
  if(/^\d{4}-\d{2}-\d{2}$/.test(input)&&parsed.toISOString().slice(0,10)!==input)return 'Date unavailable';
  if(/^\d{4}-\d{2}-\d{2}$/.test(input))return parsed.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',timeZone:'UTC'});
  return `${parsed.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',timeZone:'UTC'})} ${parsed.toISOString().slice(11,16)} UTC`;
}
export function dclCitation(citation) {
  if(typeof citation==='string')return citation;
  if(!citation || typeof citation!=='object')return '';
  return [citation.documentName || citation.fileName || citation.documentId,
    citation.documentVersion!==undefined?`version ${citation.documentVersion}`:null,
    citation.clause || citation.section,
    citation.location || (citation.page!==undefined?`page ${citation.page}`:null)].map(dclText).filter(Boolean).join(' · ');
}
export function dclSourceLabel(source) {
  if(typeof source==='string')return source;
  if(!source || typeof source!=='object')return '';
  return [source.label || source.sourceType || source.kind || source.type,
    source.reference || source.fileName || source.documentName || source.documentId || source.recordId || source.sourceId || source.id,
    source.field || source.path,
    source.rowNumber!==undefined?`row ${source.rowNumber}`:null].map(dclText).filter(Boolean).join(' · ');
}
export function getDclClauseGroups(assessment) {
  return [
    {id:'met',label:'Met',clauses:records(assessment?.metClauses)},
    {id:'failed',label:'Failed',clauses:records(assessment?.failedClauses)},
    {id:'unknown',label:'Unknown',clauses:records(assessment?.unknownClauses)},
  ];
}
function routeNodes(tree) {
  if(Array.isArray(tree))return tree.flatMap(routeNodes);
  if(!tree || typeof tree!=='object')return [];
  return [tree,...records(tree.children).flatMap(routeNodes)];
}
export function getDclMatchedRoutes(assessment) {
  const nodes=routeNodes(assessment?.routeResults);
  return records(assessment?.matchedRouteIds).map(id=>{
    const node=nodes.find(entry=>entry.id===id || entry.path===id);
    return {id:dclText(id),label:dclText(node?.label || node?.name || id),citation:dclCitation(node?.citation)};
  });
}
function attentionFor(assessment) {
  const actions=records(assessment?.missingInputActions), clauses=records(assessment?.unknownClauses), blockers=records(assessment?.blockers);
  const label=value=>typeof value==='string'?value:dclText(value?.message || value?.reason || value?.label);
  const reviewAction=actions.find(action=>action?.action==='review_policy' || action?.requiresReview===true);
  const reviewClause=clauses.find(clause=>clause?.requiresReview===true || /conflict|unsupported|ambigui|interpretation|mismatch/i.test(label(clause)));
  const kind=assessment?.attention==='needs_data'?'data':assessment?.attention==='needs_review'?'unknown':blockers.length||reviewAction||reviewClause?'unknown':actions.some(action=>action?.action==='supply_or_review_source')?'data':'unknown';
  return {kind,reason:kind==='data'?label(actions.find(action=>action?.action==='supply_or_review_source') || clauses[0]):label(blockers[0] || reviewAction || reviewClause)};
}
export function getDclPresentation(assessment, {loading=false,error} = {}) {
  let kind='unknown';
  if(loading)kind='loading';
  else if(error)kind='error';
  else if(!assessment || assessment.status==='not_assessed' || !hasPolicy(assessment))kind='unassessed';
  else if(assessment.stale===true || assessment.isStale===true || assessment.evaluationStale===true || assessment.freshness?.stale===true || assessment.freshness?.status==='stale')kind='stale';
  else if(assessment.status==='eligible_evidence')kind='eligible';
  else if(assessment.status==='criteria_not_met')kind='failed';
  else if(assessment.status==='unknown')kind=attentionFor(assessment).kind;
  const styleKind=kind==='stale'&&assessment?.attention==='needs_data'?'data':kind;
  const model={...statusStyles[styleKind],kind,policyLabel:hasPolicy(assessment)?`Policy ${assessment.policyId} · Version ${assessment.policyVersion}`:'No assessed policy version',
    limit:kind==='eligible'?assessment?.limits?.eligibleDclCap || null:null,
    assessedAt:assessment?.assessmentAt || null,
    disclaimer:dclText(assessment?.disclaimer) || 'Policy-based evidence assessment only. This is not insurer-issued approval or confirmation that cover has been issued or bound.'};
  model.limitText=formatDclMoney(model.limit);
  const stated=dclText(assessment?.reason || assessment?.explanation || assessment?.summary);
  model.reason=kind==='loading'?'Loading the recorded assessment and its policy criteria.'
    :kind==='error'?'The assessment could not be loaded. No DCL eligibility or limit is assumed.'
    :kind==='unassessed'?'No policy-based assessment is recorded for this company. No DCL eligibility or limit is assumed.'
    :kind==='stale'?(dclText(assessment?.staleReason) || stated || 'Company data or the policy version has changed. Reassessment is required before relying on the result.')
    :stated || (kind==='data'?(attentionFor(assessment).reason || 'Policy-required evidence is missing or out of date. Update only the recorded missing inputs.')
    :kind==='unknown'?(attentionFor(assessment).reason || 'Policy interpretation, source conflicts or unsupported conditions require review before eligibility can be established.')
    :kind==='eligible'?'The server assessment records eligible evidence against this policy version. This is not confirmation that cover has been issued or bound.'
    :kind==='failed'?'One or more policy criteria are not met. Review the failed clauses and their recorded evidence.'
    :'DCL eligibility is unresolved. Review the unknown clauses, missing inputs and recorded blockers.');
  return model;
}
export function getDclMissingActions(assessment) { return records(assessment?.missingInputActions); }
export function dclClauseState(clause) {
  if(clause?.state==='met')return {key:'met',label:'Met',color:'#386452'};
  if(clause?.state==='not_met')return {key:'failed',label:'Failed',color:'#964C43'};
  return {key:'unknown',label:'Unknown',color:'#876B2D'};
}

export function getInsuredLimitPresentation(value) {
  const record=value&&typeof value==='object'?value:{};
  const states={known:'Recorded insurer limit',expired:'Expired',withdrawn:'Withdrawn',declined:'Declined',unknown:'Unknown',conflicting:'Conflicting decisions',not_yet_effective:'Not yet effective'};
  const state=Object.prototype.hasOwnProperty.call(states,record.state)?record.state:'unknown';
  const historical=record.lastKnownAmount&&typeof record.lastKnownAmount==='object'?record.lastKnownAmount:record.lastKnownAmount!==null&&record.lastKnownAmount!==undefined?{amount:record.lastKnownAmount,currency:record.currency}:null;
  const amountText=state==='known'?formatDclMoney(record):'—', historicalText=formatDclMoney(historical);
  return {state,label:states[state],amountText,stateFirstText:state==='known'?amountText:`${states[state]}${historicalText!=='—'?` · ${state==='not_yet_effective'?'not current':'historical'}: ${historicalText}`:''}`,
    historicalText,historicalLabel:state==='not_yet_effective'?'Recorded future amount — not current cover':'Last recorded amount — historical only',
    reason:dclText(record.reason)||(state==='known'?'This is the separately recorded insurer decision, independent of DCL eligibility.':'No current insured amount is established by this record. Missing information does not establish zero cover.'),
    sources:records(record.sources),sourceRefs:records(record.sourceRefs),decisions:records(record.decisions)};
}
